import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Styled from '../configuration-style';
import InfoModal from './info-modal';

/**
 * Option component to render a selectable option
 * @param {object} props - The props object
 * @param {object} props.node - Current node data
 * @param {string} props.name - Name of the current field
 * @param {object} props.parentNode - The parent node data
 * @param {string} props.parentName - Name of the parent field
 * @param {Function} props.validateOption - Function to validate an option
 * @param {Function} props.renderBelowTheLine - Function to render below the line content
 * @param {object} props.infoModal - State for the info modal
 * @param {Function} props.setInfoModal - Function to set the info modal state
 * @param {Function} props.handleOptionClick - Function to  handle option click
 * @param {string} props.partNumber - Option part number
 * @returns {JSX.Element} The rendered Option component
 */
const Option = ({
  node,
  name,
  parentNode,
  parentName,
  validateOption,
  renderBelowTheLine,
  infoModal,
  setInfoModal,
  handleOptionClick,
  partNumber,
}) => {
  const { control, register } = useFormContext();
  useEffect(() => {
    register(`${name}.name`, {
      value: name,
    });
    register(`${name}.node`, {
      value: node.id,
    });
    register(`${name}.type`, {
      value: 'option',
    });
    register(`${name}.belowTheLine`, {
      value: false,
    });
    register(`${name}.partNumber`, {
      value: partNumber,
    });
  }, [name, node.id, register, partNumber]);

  const thumbnail = node?.option_price_link_thumbnail?.includes('http')
    ? node.option_price_link_thumbnail
    : node.thumbnail;

  return (
    <Controller
      shouldUnregister={false}
      rules={{
        validate: () =>
          validateOption({
            node,
            parentNode,
            parentName,
            name,
          }),
      }}
      render={({ field: { onChange, value } }) => (
        <Styled.OptionContainer>
          <Styled.OptionCardContainer
            selected={value}
            id={`${node.id}_${node.part_number}`}
            data-name={name}
            data-selectType={parentNode.select_type}
            data-required={parentNode.is_required}
            data-deps={node?.dependencies?.length > 0}
            onClick={() => {
              handleOptionClick({ onChange, value });
            }}
          >
            <Styled.OptionCard>
              {thumbnail ? <Styled.Img src={thumbnail} /> : <p>not found</p>}
            </Styled.OptionCard>
            <Styled.OptionNameContainer>
              <p>{node.name}</p>
              <Styled.IconHolder>
                <Styled.RadioDisplay
                  $selected={value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptionClick({ onChange, value });
                  }}
                />
                <Styled.ActionHolder>
                  {renderBelowTheLine()}
                  <Styled.InfoIcon
                    size={24}
                    relative
                    onClick={(e) => {
                      e.stopPropagation();
                      setInfoModal({ node, show: true });
                    }}
                  />
                </Styled.ActionHolder>
              </Styled.IconHolder>
            </Styled.OptionNameContainer>
          </Styled.OptionCardContainer>
          <InfoModal
            id={infoModal.node?.option_price_link_id}
            open={infoModal.show}
            close={() => setInfoModal({ show: false, node: null })}
            addToSelection={() => {
              handleOptionClick({ onChange, value });
              setInfoModal({ node: null, show: false });
            }}
          />
        </Styled.OptionContainer>
      )}
      name={`${name}.id`}
      control={control}
    />
  );
};

Option.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    part_number: PropTypes.string,
    dependencies: PropTypes.array,
    thumbnail: PropTypes.string,
    option_price_link_thumbnail: PropTypes.string,
  }).isRequired,
  name: PropTypes.string.isRequired,
  parentNode: PropTypes.shape({
    select_type: PropTypes.string,
    is_required: PropTypes.bool,
  }).isRequired,
  parentName: PropTypes.string.isRequired,
  validateOption: PropTypes.func.isRequired,
  renderBelowTheLine: PropTypes.func.isRequired,
  infoModal: PropTypes.shape({
    show: PropTypes.bool,
    node: PropTypes.shape({
      option_price_link_id: PropTypes.string,
    }),
  }).isRequired,
  setInfoModal: PropTypes.func.isRequired,
  handleOptionClick: PropTypes.func.isRequired,
  partNumber: PropTypes.string.isRequired,
};

export default Option;
